import React from 'react';
import { 
    Star, AlignLeft, Dumbbell, Activity, Moon, Utensils, Pill, CheckSquare, ChevronDown, ChevronRight, Loader2, Play, Sparkles, Trash2
} from 'lucide-react';
import { EVENT_TYPES, EVENT_THEMES } from '@/shared/constants/event-types.v4';
import { renderSetLabel } from '@shared/utils/formatting';
import { Event } from '@/types/zod.schemas';
import { resolveEventTemporalState } from '@/shared/logic/event-logic';

// Atomic Components
import { IconPod } from './atomic/IconPod';
import { EventStatusBadges } from './atomic/EventStatusBadges';
import { PerformancePills } from './atomic/PerformancePills';
import { NutritionSummary } from './atomic/NutritionSummary';

interface EventRowProps {
    event: Event;
    isExpanded: boolean;
    onToggle: () => void;
    onEdit: (event: Event) => void;
    onDismissGhost?: (window: string) => void;
    isLoading: boolean;
    isAnalyzing?: boolean;
    details?: any;
}

/**
 * EventRow: 100% VISUAL PARITY WITH COMMIT 7F.
 * REFACTORED: Decomposed logic and sub-components.
 * ENHANCED: Support for GHOST (Suggested) event visualization and manual dismissal.
 */
export const EventRow: React.FC<EventRowProps> = ({ event, isExpanded, onToggle, onEdit, onDismissGhost, isLoading, isAnalyzing, details }) => {
    const effectiveType = event.type;
    const themeColor = EVENT_THEMES[effectiveType] || EVENT_THEMES.TASK;

    const isCompleted = event.status === 'COMPLETED';
    const isPlanned = event.status === 'PLANNED';
    const isActive = event.status === 'ACTIVE';
    const isSkipped = event.status === 'SKIPPED';
    const isGhost = (event as any).status === 'GHOST';
    
    // TEMPORAL LOGIC (DECOUPLED)
    const { isDueNow, isOverdue } = resolveEventTemporalState(event);

    const metrics = (event.preview_data as any) || {};
    const payload = (event.payload as any) || {};
    
    // Fallback quality rating from payload if preview_data is stale
    const qualityRating = metrics.quality_rating || payload.quality_rating || 0;

    const calories = metrics.calories || payload.calories || 0;
    const protein = metrics.protein || payload.protein || 0;
    const carbs = metrics.carbs || payload.carbs || 0;
    const fat = metrics.fat || payload.fat || 0;

    const startStr = event.start_time ? new Date(event.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '';
    
    // SAFETY: Ensure end time is never before start time for the display range
    const getSafeEndTime = () => {
        if (!event.start_time) return null;
        const startMs = new Date(event.start_time).getTime();
        const endMs = event.end_time ? new Date(event.end_time).getTime() : startMs + 3600000;
        
        if (endMs <= startMs) return new Date(startMs + 60000).toISOString();
        return event.end_time || new Date(startMs + 3600000).toISOString();
    };
    
    const rawEndTime = getSafeEndTime();
    const endStr = rawEndTime ? new Date(rawEndTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '';

    const getIcon = () => {
        switch(effectiveType) {
            case EVENT_TYPES.PERFORMANCE: return Dumbbell;
            case EVENT_TYPES.MOBILITY: return Activity;
            case EVENT_TYPES.RECOVERY: return Moon;
            case EVENT_TYPES.NUTRITION: return Utensils;
            case EVENT_TYPES.SUPPLEMENTATION: return Pill;
            default: return CheckSquare;
        }
    };
    const Icon = getIcon();

    const getRecoveryDuration = () => {
        if (event.summary && event.summary !== '0h 0m' && event.summary !== '0m') return formatSmartDuration(event.summary);
        const start = new Date(event.start_time).getTime();
        const end = event.end_time ? new Date(event.end_time).getTime() : 0;
        if (end > start) {
            const diff = end - start;
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            if (h > 0) return `${h}h ${m}m`;
            if (m > 0) return `${m}m`;
            return `${s}s`;
        }
        return event.summary || null;
    };

    const formatSmartDuration = (summary: string | null | undefined) => {
        if (!summary) return null;
        const matches = summary.match(/([0-9]+)h\s*([0-9]+)m/i);
        if (matches) {
            const h = parseInt(matches[1]);
            const m = parseInt(matches[2]);
            if (h === 0) return `${m}m`;
            return `${h}h ${m}m`;
        }
        return summary.toLowerCase();
    };

    return (
        <div 
            onClick={() => onEdit(event)}
            style={{ 
                display: 'flex', flexDirection: 'column', 
                backgroundColor: isGhost ? 'rgba(255,255,255,0.01)' : (isActive || isDueNow ? `${themeColor}22` : isOverdue ? 'rgba(239, 68, 68, 0.05)' : isCompleted ? `${themeColor}0d` : 'transparent'), 
                borderTopWidth: '1px',
                borderRightWidth: '1px',
                borderBottomWidth: '1px',
                borderLeftWidth: '5px',
                borderTopStyle: (isGhost || (isPlanned && !isDueNow && !isOverdue)) ? 'dashed' : 'solid',
                borderRightStyle: (isGhost || (isPlanned && !isDueNow && !isOverdue)) ? 'dashed' : 'solid',
                borderBottomStyle: (isGhost || (isPlanned && !isDueNow && !isOverdue)) ? 'dashed' : 'solid',
                borderLeftStyle: 'solid',
                borderTopColor: isGhost ? 'rgba(255,255,255,0.05)' : (isActive || isDueNow ? themeColor : isOverdue ? '#ef4444' : isPlanned ? `${themeColor}66` : '#333'),
                borderRightColor: isGhost ? 'rgba(255,255,255,0.05)' : (isActive || isDueNow ? themeColor : isOverdue ? '#ef4444' : isPlanned ? `${themeColor}66` : '#333'),
                borderBottomColor: isGhost ? 'rgba(255,255,255,0.05)' : (isActive || isDueNow ? themeColor : isOverdue ? '#ef4444' : isPlanned ? `${themeColor}66` : '#333'),
                borderLeftColor: isGhost ? 'rgba(255,255,255,0.2)' : (isOverdue ? '#ef4444' : themeColor),
                padding: '16px', borderRadius: '14px', cursor: 'pointer', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative', overflow: 'hidden', marginBottom: '8px',
                boxShadow: isDueNow ? `0 0 20px ${themeColor}22` : isCompleted ? `0 4px 20px rgba(0,0,0,0.4)` : 'none',
                opacity: (isSkipped || isGhost) ? 0.4 : 1
            }}
            className={(isActive || isDueNow) ? 'animate-pulse hover-glow' : 'hover-glow'}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
                    <IconPod 
                        Icon={isGhost ? Sparkles : Icon} 
                        isDueNow={isDueNow} 
                        isOverdue={isOverdue} 
                        isPlanned={isPlanned} 
                        isSkipped={isSkipped} 
                        isCompleted={isCompleted} 
                        themeColor={isGhost ? '#666' : themeColor} 
                    />

                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                            <h3 style={{ fontSize: '15px', fontWeight: '900', color: isGhost ? '#888' : (isExpanded ? themeColor : '#fff'), textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0, textDecoration: isSkipped ? 'line-through' : 'none' }}>
                                {event.label}
                            </h3>
                            
                            {effectiveType === EVENT_TYPES.RECOVERY && isCompleted && (
                                <span style={{ color: themeColor, marginLeft: '8px', fontSize: '15px', fontWeight: '900' }}>
                                    {getRecoveryDuration()}
                                    {qualityRating > 0 && (
                                        <span style={{ display: 'inline-flex', gap: '2px', marginLeft: '10px', verticalAlign: 'middle' }}>
                                            {[...Array(qualityRating)].map((_, i) => <Star key={i} size={10} color={themeColor} fill={themeColor} />)}
                                        </span>
                                    )}
                                </span>
                            )}

                            {!isGhost && (
                                <EventStatusBadges 
                                    isActive={isActive} 
                                    isDueNow={isDueNow} 
                                    isOverdue={isOverdue} 
                                    themeColor={themeColor} 
                                />
                            )}
                            
                            {isGhost && (
                                <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', padding: '2px 6px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <span style={{ fontSize: '8px', fontWeight: '950', color: '#666', letterSpacing: '1px' }}>SUGGESTED</span>
                                </div>
                            )}
                        </div>
                        
                        <p style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: isOverdue ? '#ef4444' : (isPlanned && !isDueNow ? themeColor : '#777'), margin: '4px 0 0', letterSpacing: '0.5px' }}>
                            {isActive ? 'IN PROGRESS' : (startStr ? `${startStr} — ${endStr}` : 'PLANNED')}
                        </p>

                        {!isPlanned && !isSkipped && !isGhost && (effectiveType === EVENT_TYPES.PERFORMANCE || effectiveType === EVENT_TYPES.MOBILITY) && (
                            <PerformancePills 
                                eventId={event.id} 
                                metrics={metrics} 
                                themeColor={themeColor} 
                                isMobility={effectiveType === EVENT_TYPES.MOBILITY} 
                                isAnalyzing={!!isAnalyzing} 
                            />
                        )}

                        {!isPlanned && !isSkipped && !isGhost && effectiveType === EVENT_TYPES.NUTRITION && (
                            <NutritionSummary 
                                calories={calories} 
                                protein={protein} 
                                carbs={carbs} 
                                fat={fat} 
                                themeColor={themeColor} 
                            />
                        )}
                    </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    {isGhost && onDismissGhost && (
                        <button 
                            onClick={(e) => { e.stopPropagation(); onDismissGhost(event.payload?.window); }}
                            style={{ all: 'unset', padding: '8px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.03)' }}
                            className="hover:bg-red-500/10"
                        >
                            <Trash2 size={18} color="#444" />
                        </button>
                    )}
                    
                    {(isCompleted || isActive) && (effectiveType === EVENT_TYPES.PERFORMANCE || effectiveType === EVENT_TYPES.MOBILITY) && (
                        <button 
                            onClick={(e) => { e.stopPropagation(); onToggle(); }}
                            style={{ all: 'unset', padding: '8px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.03)' }}
                        >
                            {isExpanded ? <ChevronDown size={20} color={themeColor} /> : <ChevronRight size={20} color="#444" />}
                        </button>
                    )}
                    {isPlanned && !isSkipped && !isGhost && (
                        <Play size={18} color={themeColor} style={{ opacity: 0.5 }} />
                    )}
                </div>
            </div>

            {isExpanded && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-200" style={{ marginTop: '16px', borderTop: '1px solid #222', paddingTop: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {isLoading && !details ? (
                            <div className="flex flex-row items-center justify-center p-5"><Loader2 className="animate-spin" color={themeColor} size={16} /></div>
                        ) : details?.groupedLogs?.map((group: any, gi: number) => (
                            <div key={gi}>
                                <div style={{ fontSize: '12px', color: '#fff', fontWeight: '900', textTransform: 'uppercase', marginBottom: '6px', opacity: 0.9 }}>{group.name}</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {group.sets.map((s: any, si: number) => (
                                        <div key={si} style={{ border: `1px solid ${themeColor}33`, padding: '3px 8px', borderRadius: '6px', backgroundColor: 'rgba(0,0,0,0.4)', fontSize: '11px', color: '#fff' }}>
                                            {renderSetLabel(s.weight, s.reps)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* NOTES PREVIEW */}
            {isCompleted && effectiveType === EVENT_TYPES.RECOVERY && (metrics.notes || payload.notes) && (
                <div style={{ display: 'flex', gap: '10px', padding: '10px 12px', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '10px', marginTop: '14px', border: `1px solid ${themeColor}11` }}>
                    <AlignLeft size={14} color={themeColor} style={{ marginTop: '2px', flexShrink: 0, opacity: 0.7 }} />
                    <p style={{ fontSize: '12px', color: '#999', margin: 0, fontStyle: 'italic', lineHeight: '1.5' }}>{metrics.notes || payload.notes}</p>
                </div>
            )}
        </div>
    );
};
